/**
 * CTRL_09 Dashboard - Data Residency & Sovereignty
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, Empty, Progress, Alert } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  GlobalOutlined,
  SwapOutlined,
  LockOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { getControlResults, getControlEvents } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

const COMPLIANCE_ZONES = {
  'EU': { label: 'European Union', color: '#1890ff' },
  'US': { label: 'United States', color: '#52c41a' },
  'APAC': { label: 'Asia Pacific', color: '#722ed1' },
  'LATAM': { label: 'Latin America', color: '#fa8c16' },
  'MENA': { label: 'Middle East & North Africa', color: '#13c2c2' },
  'UNKNOWN': { label: 'Unknown', color: '#888' }
};

function CTRL_09_Dashboard({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [events, setEvents] = useState([]);
  const controlCode = 'CTRL_09';

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resultsRes, eventsRes] = await Promise.all([
        getControlResults(projectId),
        getControlEvents(projectId, controlCode).catch(() => ({ data: { events: [] } }))
      ]);

      const controlResults = (resultsRes.data.results || []).find(r => r.control_code === controlCode);
      setResults(controlResults);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate geographic distribution
  const geoDistribution = useMemo(() => {
    const distribution = {};

    events.forEach(e => {
      const zone = e.event_metadata?.compliance_zone || 'UNKNOWN';
      distribution[zone] = (distribution[zone] || 0) + 1;
    });

    return Object.entries(distribution).map(([zone, count]) => ({
      zone,
      label: COMPLIANCE_ZONES[zone]?.label || zone,
      color: COMPLIANCE_ZONES[zone]?.color || '#888',
      count,
      percentage: events.length > 0 ? Math.round((count / events.length) * 100) : 0
    }));
  }, [events]);

  // Calculate cross-border transfers
  const crossBorderStats = useMemo(() => {
    const crossBorderEvents = events.filter(e => e.event_metadata?.cross_border_transfer === true);

    const legalBasisCounts = {};
    crossBorderEvents.forEach(e => {
      const basis = e.event_metadata?.legal_basis || 'None';
      legalBasisCounts[basis] = (legalBasisCounts[basis] || 0) + 1;
    });

    const encrypted = crossBorderEvents.filter(e => e.event_metadata?.encryption_in_transit === true).length;

    return {
      total: crossBorderEvents.length,
      percentage: events.length > 0 ? Math.round((crossBorderEvents.length / events.length) * 100) : 0,
      encrypted,
      encryptionRate: crossBorderEvents.length > 0 ? Math.round((encrypted / crossBorderEvents.length) * 100) : 0,
      legalBasis: Object.entries(legalBasisCounts).map(([basis, count]) => ({ basis, count }))
    };
  }, [events]);

  // Calculate compliance by region
  const complianceByRegion = useMemo(() => {
    const regionCompliance = {};

    events.forEach(e => {
      const region = e.event_metadata?.source_region || 'Unknown';
      if (!regionCompliance[region]) {
        regionCompliance[region] = { total: 0, compliant: 0, violations: 0 };
      }

      regionCompliance[region].total++;

      // Check compliance (simplified - based on legal basis and encryption)
      const crossBorder = e.event_metadata?.cross_border_transfer;
      const legalBasis = e.event_metadata?.legal_basis;
      const encrypted = e.event_metadata?.encryption_in_transit;

      if (crossBorder && (!legalBasis || legalBasis === 'None' || !encrypted)) {
        regionCompliance[region].violations++;
      } else {
        regionCompliance[region].compliant++;
      }
    });

    return Object.entries(regionCompliance).map(([region, stats]) => ({
      region,
      total: stats.total,
      compliant: stats.compliant,
      violations: stats.violations,
      complianceRate: Math.round((stats.compliant / stats.total) * 100)
    }));
  }, [events]);

  // Table columns for recent transfers
  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'ts',
      key: 'ts',
      render: (ts) => new Date(ts).toLocaleString(),
      width: 180
    },
    {
      title: 'Source Region',
      dataIndex: ['event_metadata', 'source_region'],
      key: 'source',
      render: (region) => region || '-'
    },
    {
      title: 'Destination Region',
      dataIndex: ['event_metadata', 'destination_region'],
      key: 'dest',
      render: (region) => region || '-'
    },
    {
      title: 'Compliance Zone',
      dataIndex: ['event_metadata', 'compliance_zone'],
      key: 'zone',
      render: (zone) => {
        const zoneInfo = COMPLIANCE_ZONES[zone] || COMPLIANCE_ZONES['UNKNOWN'];
        return <Tag color={zoneInfo.color}>{zoneInfo.label}</Tag>;
      }
    },
    {
      title: 'Cross-Border',
      key: 'cross_border',
      align: 'center',
      render: (_, record) => {
        const isCrossBorder = record.event_metadata?.cross_border_transfer === true;
        return isCrossBorder ?
          <Tag color="orange" icon={<SwapOutlined />}>Yes</Tag> :
          <Tag color="green">No</Tag>;
      }
    },
    {
      title: 'Legal Basis',
      dataIndex: ['event_metadata', 'legal_basis'],
      key: 'legal_basis',
      render: (basis) => basis || '-'
    },
    {
      title: 'Encrypted',
      key: 'encrypted',
      align: 'center',
      render: (_, record) => {
        const encrypted = record.event_metadata?.encryption_in_transit === true;
        return encrypted ?
          <Tag color="green" icon={<LockOutlined />}>Yes</Tag> :
          <Tag color="red" icon={<CloseCircleOutlined />}>No</Tag>;
      }
    }
  ];

  // Compliance by region table columns
  const complianceColumns = [
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region'
    },
    {
      title: 'Total Events',
      dataIndex: 'total',
      key: 'total',
      align: 'center'
    },
    {
      title: 'Compliant',
      dataIndex: 'compliant',
      key: 'compliant',
      align: 'center'
    },
    {
      title: 'Violations',
      dataIndex: 'violations',
      key: 'violations',
      align: 'center',
      render: (violations) => violations > 0 ?
        <Tag color="red">{violations}</Tag> :
        <Tag color="green">0</Tag>
    },
    {
      title: 'Compliance Rate',
      dataIndex: 'complianceRate',
      key: 'complianceRate',
      align: 'center',
      render: (rate) => (
        <div style={{ width: '120px' }}>
          <Progress
            percent={rate}
            size="small"
            strokeColor={rate >= 90 ? '#52c41a' : rate >= 70 ? '#faad14' : '#f5222d'}
          />
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!projectId) {
    return (
      <Empty
        description="Please select a project to view dashboard"
        style={{ padding: '40px' }}
      />
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>CTRL_09: Data Residency & Sovereignty Dashboard</h2>

      {/* Cross-Border Transfer Alert */}
      {crossBorderStats.total > 0 && crossBorderStats.encryptionRate < 100 && (
        <Alert
          message={`${crossBorderStats.total - crossBorderStats.encrypted} Unencrypted Cross-Border Transfer(s)`}
          description="Some cross-border transfers are not encrypted. All cross-border data transfers must be encrypted in transit."
          type="warning"
          icon={<WarningOutlined />}
          showIcon
          closable
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Events"
              value={events.length}
              prefix={<GlobalOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Cross-Border Transfers"
              value={crossBorderStats.percentage}
              suffix="%"
              prefix={<SwapOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {crossBorderStats.total} transfers
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Encryption Rate"
              value={crossBorderStats.encryptionRate}
              suffix="%"
              prefix={<LockOutlined />}
              valueStyle={{ color: crossBorderStats.encryptionRate === 100 ? '#52c41a' : '#fa8c16' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {crossBorderStats.encrypted} of {crossBorderStats.total} encrypted
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Compliance Zones"
              value={geoDistribution.length}
              prefix={<GlobalOutlined />}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Geographic Distribution */}
      <Card title="Geographic Distribution by Compliance Zone" style={{ marginBottom: '24px' }}>
        <Row gutter={16}>
          {geoDistribution.map((zone, idx) => (
            <Col xs={24} sm={12} md={8} key={idx} style={{ marginBottom: '16px' }}>
              <Card size="small" style={{ backgroundColor: `${zone.color}15`, borderColor: zone.color }}>
                <Statistic
                  title={zone.label}
                  value={zone.count}
                  suffix={`(${zone.percentage}%)`}
                  valueStyle={{ color: zone.color }}
                />
                <Progress
                  percent={zone.percentage}
                  strokeColor={zone.color}
                  size="small"
                  style={{ marginTop: '8px' }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Compliance by Region */}
      <Card title="Compliance by Region" style={{ marginBottom: '24px' }}>
        <Table
          dataSource={complianceByRegion}
          columns={complianceColumns}
          rowKey="region"
          pagination={false}
          size="small"
        />
      </Card>

      {/* Legal Basis Breakdown */}
      {crossBorderStats.legalBasis.length > 0 && (
        <Card title="Cross-Border Transfer Legal Basis" style={{ marginBottom: '24px' }}>
          <Row gutter={16}>
            {crossBorderStats.legalBasis.map((item, idx) => (
              <Col xs={24} sm={12} key={idx} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 'bold' }}>{item.basis}</span>
                  <span>{item.count} transfers</span>
                </div>
                <Progress
                  percent={Math.round((item.count / crossBorderStats.total) * 100)}
                  size="small"
                  strokeColor="#1890ff"
                />
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Recent Transfers Table */}
      <Card title="Recent Data Transfers">
        <Table
          dataSource={events.slice(0, 20)}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} transfers`
          }}
          size="small"
        />
      </Card>
    </div>
  );
}

export default CTRL_09_Dashboard;
